import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { HoverCard } from "radix-ui";
import { faDesktopArrowDown, faPlus } from "@fortawesome/pro-solid-svg-icons";
import {faPen, faFileMinus, faTrash, faText, faEye, faLink, faSave} from "@fortawesome/pro-regular-svg-icons";
import {t} from "i18next";

import Card from "@/components/card";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import PromptDialog from "@/components/dialogs/prompt-dialog";
import LoadingButton from "@/components/buttons/loading-button";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";

import useExtractionContext from "@/pages/takeoff/layouts/extraction/context/use-extraction-context.ts";
import useProjectIndicatorsContext from "@/pages/takeoff/context/project-indicators/use-project-indicators-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";

import {KeyDisplayPair} from "@/models/interfaces/key-value.ts";
import ExtractionOperation, { operationMap } from "@/pages/takeoff/layouts/extraction/context/models/enums/extraction-operation.ts";
import {SelectionCardRef} from "./selection-card";
import ExtractionNodeElement from "@/pages/takeoff/layouts/extraction/context/models/interfaces/extraction-node-element.ts";
import ExtractionSheetLeafNode from "@/pages/takeoff/layouts/extraction/context/models/extraction-sheet-leaf-node.ts";
import ExtractionSheetArticleNode from "@/pages/takeoff/layouts/extraction/context/models/extraction-sheet-article-node.ts";
import ExtractionSheetArticleElementBase
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-element-base.ts";
import ExtractionSheetArticleMeasuresElement
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-measures-element.ts";
import ExtractionSheetArticle3DElement
    from "@/pages/takeoff/layouts/extraction/context/models/article-elements/extraction-sheet-article-3D-element.ts";
import LoadingButtonLockDependant from "@/components/buttons/loading-button-lock-dependant";
import Tooltip from "@/components/tooltip";

interface ExtractionCardProps {
    selectionCardRef: React.RefObject<SelectionCardRef>;
    isViewerLoading?: boolean;
}

const ExtractionCard: React.FC<ExtractionCardProps> = ({ selectionCardRef, isViewerLoading }) => {

    const {
        projectSheets,
        isLoading, isSaving, isDirty,
        addSheet, updateSheetName, removeSheet,
        addNode, updateNodeName, addSubNode, removeNode,
        addArticle, updateArticleName, linkMeasuresToArticle, link3DToArticle, updateArticleData, removeArticle,
        exportExcel, saveSheets
    } = useExtractionContext();

    const { projectMeasures } = useProjectMeasureContext();
    const { projectIndicatorsData } = useProjectIndicatorsContext();
    
    const { setSaveCallback } = usePreventNavigationContext();
    
    const [selectedSheetId, setSelectedSheetId] = useState<string | null>(null);

    const selectedSheet = useMemo(() => projectSheets?.sheets.find(sheet => sheet.id === selectedSheetId) ?? null, [projectSheets, selectedSheetId]);

    const handleLinkMeasures = useCallback((articleId: string, measureTypeId: string) => {
        if (!selectedSheetId || selectionCardRef.current == null) return;
        const selectedStoreys = selectionCardRef.current.getSelectedStoreys();
        
        if(selectedStoreys == null || measureTypeId == null) return;
        linkMeasuresToArticle(selectedSheetId, articleId, measureTypeId, selectedStoreys);
    }, [selectedSheetId, selectionCardRef, linkMeasuresToArticle]);

    const handleLink3D = useCallback((articleId: string, indicatorDef: string) => {
        if (!selectedSheetId || selectionCardRef.current == null) return;
        const selectedStoreys = selectionCardRef.current.getSelectedStoreys();

        if (selectedStoreys == null || !projectMeasures || !projectIndicatorsData || indicatorDef == null) return;

        const selectedElements = selectedStoreys
            .map(storeyId => projectMeasures.getBuildingAndStoreyFromStoreyId(storeyId)).filter(e => e != null);
        
        const ids: number[] = [];
        
        Array.from(selectedElements).forEach(element => {
            const building = projectIndicatorsData.buildings.find(b => b.buildingName === element.building.name)
            const storey = building?.storeys.find(s => s.storeyName === element.storey.name);
            if (storey) {
                ids.push(storey.storeyId);
            }
        })
        
        link3DToArticle(selectedSheetId, articleId, indicatorDef, ids);
    }, [selectedSheetId, selectionCardRef, projectMeasures, projectIndicatorsData, link3DToArticle]);
    
    const handleLink = useCallback((articleId: string) => {
        if (!selectedSheetId || selectionCardRef.current == null) return;
        
        const measureTypeId = selectionCardRef.current.getSelectedMeasureType()?.measureTypeId;
        if(measureTypeId != null) {
            handleLinkMeasures(articleId, measureTypeId);
            return;
        }

        const indicatorDef = selectionCardRef.current.getSelectedIndicatorDef();
        if(indicatorDef != null) {
            handleLink3D(articleId, indicatorDef);
        }
    }, [selectedSheetId, selectionCardRef, handleLink3D, handleLinkMeasures]);
    
    const handleDisplayStoreys = useCallback((ae: ExtractionSheetArticleElementBase) => {
        if (selectionCardRef.current == null) return;
        
        if(ae instanceof ExtractionSheetArticleMeasuresElement) {
            selectionCardRef.current.setSelectedStoreys(ae.associatedMeasureTypeId, ae.associatedStoreysIds);
        } else if (ae instanceof ExtractionSheetArticle3DElement){
            selectionCardRef.current.setSelectedStoreysFrom3D(ae.dataKey, ae.associatedStoreysIds);
        }
    }, [selectionCardRef]);
    
    // Sélection de la feuille nouvellement créée
    const sheetsNumber = useRef<number>(0);
    
    useEffect(() => {
        if(projectSheets?.sheets == null || projectSheets.sheets.length === 0) return;
        if(projectSheets.sheets.length !== sheetsNumber.current) {
            sheetsNumber.current = projectSheets.sheets.length;
            setSelectedSheetId(projectSheets.sheets[projectSheets.sheets.length - 1].id);
        }
    }, [projectSheets?.sheets]);
    
    // Gestion export excel
    const [isExporting, setIsExporting] = useState(false);
    
    const handleExportExcel = useCallback(async () => {
        if(selectedSheet == null) return;
        setIsExporting(true);
        await exportExcel(selectedSheet.id);
        setIsExporting(false);
    }, [selectedSheet, exportExcel])
    
    /* GESTION DES PROMPTS ARTICLES ET NOEUDS */

    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [promptTitle, setPromptTitle] = useState('');
    const [promptValue, setPromptValue] = useState('');
    const [onConfirmAction, setOnConfirmAction] = useState<((value: string) => void) | null>(null);

    const openPrompt = useCallback((title: string, initialValue: string, onConfirm: (value: string) => void) => {
        setPromptTitle(title);
        setPromptValue(initialValue);
        setOnConfirmAction(() => onConfirm);
        setIsPromptOpen(true);
    }, []);

    const handlePromptConfirm = useCallback(() => {
        if (onConfirmAction) {
            onConfirmAction(promptValue);
        }
        setIsPromptOpen(false);
    }, [onConfirmAction, promptValue]);

    const handlePromptCancel = useCallback(() => {
        setIsPromptOpen(false);
    }, []);
    
    /* GESTION PROMPT MODIFICATION ELEMENT ARTICLE */
    
    const [isArticleElementPromptOpen, setIsArticleElementPromptOpen] = useState(false);
    const [selectedArticleElement, setSelectedArticleElement] = useState<ExtractionSheetArticleElementBase | null>(null);
    const [dataTypes, setDataTypes] = useState<KeyDisplayPair<string, string>[] | null>(null);
    
    const [promptDataTypeResult, setPromptDataTypeResult] = useState<string | null>(null);
    const [promptExtractionOperationResult, setPromptExtractionOperationResult] = useState<ExtractionOperation | null>(null);
    
    const openArticleElementPrompt = useCallback((ae: ExtractionSheetArticleElementBase) => {
        setSelectedArticleElement(ae);
        setPromptDataTypeResult(ae.dataKey);
        setPromptExtractionOperationResult(ae.operation);
        
        if(ae instanceof ExtractionSheetArticleMeasuresElement) {
            const {dataInformations} = ae.associatedMeasures[0];

            const informations: KeyDisplayPair<string, string>[] = [];
            if (typeof dataInformations === "object" && !Array.isArray(dataInformations) && !(dataInformations instanceof Map)) {
                const entries = Object.entries(dataInformations as Record<string, unknown>);
                for (let i = 0; i < entries.length; i += 1) {
                    const [key] = entries[i];
                    if (key !== "color" && key !== "alpha") {
                        const translatedValue = t(key as string);
                        informations.push({key, display: translatedValue});
                    }
                }
            }
            setDataTypes(informations);
        } else {
            setDataTypes(null);
        }
        
        setIsArticleElementPromptOpen(true);
    }, []);
    
    const handleArticleElementPromptConfirm = useCallback(() => {
        if (selectedSheet == null || selectedArticleElement == null || promptDataTypeResult == null || promptExtractionOperationResult == null) return;
        updateArticleData(selectedSheet.id, selectedArticleElement.id, promptDataTypeResult, promptExtractionOperationResult);
        setIsArticleElementPromptOpen(false);
    }, [promptDataTypeResult, promptExtractionOperationResult, selectedArticleElement, selectedSheet, updateArticleData]);
    
    const handleArticleElementPromptCancel = useCallback(() => {
        setIsArticleElementPromptOpen(false);
    }, []);
    
    /* GESTION OVER */
    const [overedElement, setOveredElement] = useState<string | undefined>(undefined);
    
    const getOverClasses = useCallback((elementId: string) => 
        overedElement === elementId
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none", [overedElement]);
    
    const renderChild = useCallback((child: ExtractionNodeElement) => {
        let content = null;
        
        if (child instanceof ExtractionSheetLeafNode) {
            content = (
                <>
                    <div className='flex flex-row gap-x-2' onMouseEnter={() => setOveredElement(child.id)}>
                        <div className='flex-1 flex flex-row gap-x-2 items-center'>
                            <span>{child.name}</span>
                        </div>

                        <div className='flex flex-row gap-x-0.5'>
                            <ButtonLockDependent 
                                variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Renommer le chapitre'
                                onClick={() => openPrompt('Renommer le chapitre', child.name, (name) => updateNodeName(selectedSheet!.id, child.id, name))} 
                                className={getOverClasses(child.id)}>
                                <div className='flex flex-row items-end'>
                                    <FontAwesomeIcon icon={faText} />
                                    <FontAwesomeIcon icon={faPen} className='text-2xs -ml-1'/>
                                </div>
                            </ButtonLockDependent>
                            
                            <ButtonLockDependent 
                                variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600}  data-tooltip-content='Supprimer le chapitre'
                                className={getOverClasses(child.id)} onClick={() => removeNode(selectedSheet!.id, child.id)}>
                                <FontAwesomeIcon icon={faTrash} className='text-red-500'/>
                            </ButtonLockDependent>

                            <HoverCard.Root openDelay={600} closeDelay={250}>
                                <HoverCard.Trigger asChild>
                                    <button disabled={isSaving || isViewerLoading} 
                                            data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Ajouter un élément'
                                            className='inline-flex items-center gap-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowedtext-gray-700 hover:bg-gray-50 px-1.5 py-0.5 text-xs'>
                                        <FontAwesomeIcon icon={faPlus} className='text-green-500'/>
                                    </button>
                                </HoverCard.Trigger>
                                <HoverCard.Portal>
                                    <HoverCard.Content
                                        className="z-3 rounded-md bg-neutral-100 p-2 shadow data-[side=bottom]:animate-slideUpAndFade data-[state=open]:transition-all"
                                        sideOffset={5} side='bottom'>
                                        <div className='flex flex-col gap-y-2'>
                                            <ButtonLockDependent variant='ghost' size='2xs' disabled={isSaving || isViewerLoading} 
                                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Ajouter un chapitre'
                                                                 onClick={() => openPrompt('Ajouter un chapitre', '', (name) => addSubNode(selectedSheet!.id, child.id, name))}>
                                                Chapitre
                                            </ButtonLockDependent>
                                            <ButtonLockDependent variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Ajouter un article'
                                                                 onClick={() => openPrompt('Ajouter un article', '', (name) => addArticle(selectedSheet!.id, child.id, name))}>
                                                Article
                                            </ButtonLockDependent>
                                        </div>
                                        <HoverCard.Arrow className='fill-neutral-100' width={16} height={8}/>
                                    </HoverCard.Content>
                                </HoverCard.Portal>
                            </HoverCard.Root>
                        </div>
                    </div>

                    {child.children.length > 0 && (
                        <div className="flex flex-col gap-y-2 border-l pl-4">
                            {child.children.map((subChild) => (
                                <React.Fragment key={subChild.id}>
                                    {renderChild(subChild)}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </>
            );
        }

        if (child instanceof ExtractionSheetArticleNode) {
            content = (
                <div className='flex flex-col gap-y-1'>
                    <div className='flex flex-row gap-x-2' onMouseEnter={() => setOveredElement(child.id)}>
                        <span className='flex-1 font-semibold'>{child.name}</span>

                        <div className='flex flex-row gap-x-0.5'>
                            <ButtonLockDependent variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content="Renommer l'article"
                                                 className={getOverClasses(child.id)}
                                                 onClick={() => openPrompt('Renommer l\'article', child.name, (name) => updateArticleName(selectedSheet!.id, child.id, name))}>
                                <div className='flex flex-row items-end'>
                                    <FontAwesomeIcon icon={faText} />
                                    <FontAwesomeIcon icon={faPen} className='text-2xs -ml-1'/>
                                </div>
                            </ButtonLockDependent>

                            <ButtonLockDependent variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content="Supprimer l'article"
                                                 className={getOverClasses(child.id)}
                                                 onClick={() => removeArticle(selectedSheet!.id, child.id)}>
                                <FontAwesomeIcon icon={faTrash} className='text-red-500'/>
                            </ButtonLockDependent>
                            
                            <ButtonLockDependent variant='ghost' size='2xs' disabled={isSaving || isViewerLoading}
                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content="Lier des métrés"
                                                 onClick={() => handleLink(child.id)}>
                                <FontAwesomeIcon icon={faLink} className='text-blue-500'/>
                            </ButtonLockDependent>
                        </div>
                    </div>

                    {child.children.length > 0 && (
                        <div className='flex flex-col gap-y-1 text-xs border-l'>
                            {child.children.map((ae) => (
                                <div className='flex flex-row gap-x-3 pl-4 items-center' key={ae.id} onMouseEnter={() => setOveredElement(ae.id)}>
                                    <span>{Math.round(ae.value * 10) / 10}</span>
                                    <span>{ae.unity}</span>
                                    
                                    <ButtonLockDependent variant='ghost' size='2xs'
                                                          data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content="Modifier le calcul"
                                                          disabled={isSaving || isViewerLoading}
                                                          className={getOverClasses(ae.id)}
                                                          onClick={() => openArticleElementPrompt(ae)}>
                                        <FontAwesomeIcon icon={faPen}/>
                                    </ButtonLockDependent>
                                    
                                    <div className='flex flex-row gap-x-0.5 ml-auto items-center'>
                                        <span className='text-gray-700'>( x {child.children.reduce((accumulator, sheet) => accumulator + sheet.elements, 0)} )</span>

                                        <ButtonLockDependent variant='ghost' size='2xs'
                                                             data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content="Afficher la liaison"
                                                             disabled={isSaving || isViewerLoading}
                                                             onClick={() => handleDisplayStoreys(ae)}>
                                            <FontAwesomeIcon icon={faEye} className='text-emerald-400'/>
                                        </ButtonLockDependent>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className='flex flex-col gap-y-2' key={child.id}>
                {content}
            </div>
        );
    }, [isSaving, isViewerLoading, getOverClasses, selectedSheet, openPrompt, updateNodeName, addSubNode, addArticle, removeNode, updateArticleName, handleLink, removeArticle, openArticleElementPrompt, handleDisplayStoreys]);

    useEffect(() => {
        setSaveCallback(saveSheets);
        return () => {
            setSaveCallback(undefined);
        };
    }, [saveSheets, setSaveCallback]);

    return (
        <>
            <Tooltip id="extraction-card-tooltip" className='z-50' place='bottom' />
            <Card additionalClasses='ml-auto h-fit min-h-40 min-w-100 overflow-y-hidden max-h-full' >
                <div className='flex flex-row gap-x-2 px-4 py-2 border-b'>
                    <div className='text-sm font-semibold text-gray-800 uppercase flex-1'>Extraction</div>

                    <ButtonLockDependent variant='simple' size='xs' disabled={isSaving || isViewerLoading}
                                         data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Créer une feuille'
                                         onClick={() => openPrompt('Créer une feuille', '', (name) => addSheet(name))}>
                        <FontAwesomeIcon icon={faPlus} className='text-green-500'/>
                    </ButtonLockDependent>
                    
                    <LoadingButton variant='simple' size='xs' onClick={handleExportExcel}
                                   data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Exporter en Excel'
                                   isLoading={isExporting} disabled={projectSheets == null || projectSheets.sheets.length === 0}
                                   body={<FontAwesomeIcon icon={faDesktopArrowDown} />} />
                    
                    <LoadingButtonLockDependant size='xs' onClick={saveSheets} disabled={!isDirty || isSaving} isLoading={isSaving}>
                        <FontAwesomeIcon icon={faSave} className='text-sm'/>
                    </LoadingButtonLockDependant>
                </div>
                {isLoading ? (
                    <SimpleLoadingIndicator text='Chargement des extractions' className='m-auto px-4'/>
                ) : (
                <>
                    {(projectSheets != null && projectSheets.sheets.length > 0) ? (
                        <div className='flex flex-col overflow-y-hidden'>
                            <div className='flex flex-row w-full gap-x-2 px-4 py-2'
                                 onMouseEnter={() => setOveredElement("SheetSelector")}>
                                <Select options={projectSheets.sheets}
                                        value={selectedSheet}
                                        onChange={(newValue) => setSelectedSheetId(newValue?.id ?? null)}
                                        isMulti={false}
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={(option) => option.name}
                                        isSearchable={false}
                                        placeholder='Sélectionner une feuille'
                                        className='flex-1'
                                        classNames={{
                                            control: () => "!min-h-0 !rounded-md !border-gray-200",
                                            menu: () => "text-sm",
                                            menuList: () => "text-sm",
                                            singleValue: () => "text-sm",
                                            placeholder: () => "text-sm",
                                            indicatorsContainer: () => "-my-1",
                                        }}
                                        menuPosition="fixed"/>

                                <div className={`flex flex-row gap-x-0.5 ${getOverClasses("SheetSelector")}`}>
                                    <ButtonLockDependent variant='ghost' size='xs' disabled={selectedSheet == null || isSaving || isViewerLoading}
                                                         data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Renommer la feuille'
                                                         onClick={() => openPrompt('Renommer la feuille', selectedSheet!.name, (name) => updateSheetName(selectedSheet!.id, name))}>
                                        <div className='flex flex-row items-end'>
                                            <FontAwesomeIcon icon={faText}/>
                                            <FontAwesomeIcon icon={faPen} className='text-2xs -ml-1'/>
                                        </div>
                                    </ButtonLockDependent>

                                    <ButtonLockDependent variant='ghost' size='xs' disabled={selectedSheet == null || isSaving || isViewerLoading}
                                                         data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Supprimer la feuille'
                                                         onClick={() => removeSheet(selectedSheet!.id)}>
                                        <FontAwesomeIcon icon={faFileMinus} className='text-red-500'/>
                                    </ButtonLockDependent>
                                </div>
                            </div>
                            
                            {(selectedSheet != null) && (
                                <div className='flex flex-col gap-y-2 px-4 py-2 overflow-y-auto max-h-full text-sm'>
                                    {selectedSheet.children.map((child) => (
                                        <div key={child.id} className='flex flex-col gap-y-1'>
                                            {renderChild(child)}
                                        </div>
                                    ))}

                                    <ButtonLockDependent variant='simple' size='xs' className='mr-auto' disabled={isSaving || isViewerLoading}
                                                         data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Ajouter un chapitre'
                                                         onClick={() => openPrompt('Ajouter un chapitre', '', (name) => addNode(selectedSheet.id, name))}>
                                        <FontAwesomeIcon icon={faPlus} className='text-green-500'/>
                                    </ButtonLockDependent>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className='flex flex-col gap-y-4 m-auto'>
                            <div className='text-orange-800'>Aucune feuille d'extraction disponible</div>
                            <ButtonLockDependent className='w-full flex' disabled={isViewerLoading} 
                                                 data-tooltip-id='extraction-card-tooltip' data-tooltip-delay-show={600} data-tooltip-content='Créer une nouvelle feuille'
                                                 onClick={() => openPrompt('Créer une feuille', '', (name) => addSheet(name))}>
                                <span className='mx-auto'>Créer une nouvelle feuille</span>
                            </ButtonLockDependent>
                        </div>
                    )}
                </>
            )}
            </Card>
            
            <PromptDialog open={isPromptOpen} title={promptTitle} onConfirm={handlePromptConfirm} onClose={handlePromptCancel}>
                <input
                    autoFocus
                    type='text'
                    value={promptValue}
                    onChange={(e) => setPromptValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePromptConfirm()}
                    className='mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-800 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                />
            </PromptDialog>
            
            <PromptDialog open={isArticleElementPromptOpen} title={`Définition du calcul ${selectedSheet?.getHierarchy(selectedArticleElement?.id ?? '')?.at(-1)}`} onConfirm={handleArticleElementPromptConfirm} onClose={handleArticleElementPromptCancel}>
                {selectedSheet && selectedArticleElement && (
                    <div className='flex flex-col gap-y-4 mt-2 text-gray-700 pt-2'>
                        
                        <div className='flex flex-row flex-1 text-xs text-gray-500'>
                            <span>{selectedSheet.getHierarchy(selectedArticleElement.id)?.join(' > ')}</span>
                            <span className='ml-auto'>Objets liés ( x {selectedArticleElement.elements} )</span>
                        </div>

                        <div className='grid grid-cols-[auto_1fr] grid-rows-auto items-center gap-x-4 gap-y-4'>
                            <label>Type de calcul</label>

                            <Select options={operationMap}
                                    value={operationMap.find(option => option.value === promptExtractionOperationResult) ?? null}
                                    onChange={(option) => setPromptExtractionOperationResult(option?.value ?? null)}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 4 }),
                                    }}
                            />
                            
                            {dataTypes && <>
                                    <label>Valeur</label>

                                    <Select options={dataTypes}
                                            value={dataTypes.find(option => option.key === promptDataTypeResult) ?? null}
                                            onChange={(option) => setPromptDataTypeResult(option?.key ?? null)}
                                            getOptionValue={(option) => option.key}
                                            getOptionLabel={(option) => option.display}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder='Sélectionner un type de donnée'
                                            styles={{
                                                menuPortal: (base) => ({...base, zIndex: 4}),
                                            }}
                                    />
                                </>
                            }
                        </div>

                    </div>
                )}
            </PromptDialog>
        </>
    )
};

export default ExtractionCard;